import { StrictMode, useMemo } from 'react';
import { createBrowserHistory, createRouter, RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import { z } from 'zod/v4';
import { QueryClientProvider } from '@interxfi/api-client';
import { AuthorizationProvider } from '@interxfi/api-client/authorization-context';
import { getQueryClient, useRealmAuthIsAuthenticated, useRealmAuthIsAuthenticating } from '@interxfi/api-client/context';
import { useEventPoller } from '@interxfi/api-client/event-polling';
import { useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { resolveRouteTabGroups } from '@interxfi/app-components/components/router-tabbed-navigation/route-meta';
import { UnexpectedError } from '@interxfi/app-components/components/unexpected-error/unexpected-error';
import { WrappedAccessProvider } from '@interxfi/app-components/context/access-context';
import { WrappedThemeProvider } from '@interxfi/app-components/context/theme-context';
import { WrappedTimeZoneProvider } from '@interxfi/app-components/context/time-zone-context';
import { RecommendationNagRail } from '@interxfi/design-system/components/recommendation-nag/recommendation-nag';
import { ToastRail } from '@interxfi/design-system/components/toast/toast';
import { NotFound } from './components/not-found-layout/not-found';
import type {
  PortalTabbedNavigationGroups,
  PortalTabbedNavigationTab,
  TabbedNavigationGroup,
  TabbedNavigationSubGroup,
} from './components/router/router-tabbed-navigation';
import { routeTree } from './routeTree.gen';
import { DEFAULT_UNAUTHENTICATED_ROUTE, MFA_SETUP_ROUTE } from './util/routing/defaults';
import { getRedirectParameter } from './util/routing/redirect';
import styles from './main.module.scss';
import '@interxfi/design-system/styles/_font.scss';
import '@interxfi/design-system/styles/global.scss';
import '@interxfi/design-system/util/polyfill';

z.config({ jitless: true });

const history = createBrowserHistory();

// Create a new router instance
const router = createRouter({
  routeTree,
  history,
  context: {
    history,
    queryClient: getQueryClient(),
    apiContext: undefined!,
    tabGroups: {},
    isAuthenticated: false,
    isAuthenticating: true,
  },
  notFoundMode: 'fuzzy',
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: UnexpectedError,
  defaultPreloadStaleTime: 0,
  defaultPreload: 'intent',
  scrollRestorationBehavior: 'instant',
  scrollToTopSelectors: ['main'],
  scrollRestoration: false,
  getScrollRestorationKey: (location) => `${location.pathname}_${location.searchStr}_${location.hash}`,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function WrappedRouter() {
  const isAuthenticated = useRealmAuthIsAuthenticated();
  const isAuthenticating = useRealmAuthIsAuthenticating();
  const apiContext = useActiveRealmAPIContext();

  const values = useMemo(
    () => ({
      queryClient: getQueryClient(),
      apiContext,
      isAuthenticated,
      isAuthenticating,
      history,
      tabGroups: resolveRouteTabGroups(
        Object.values(router.routesById).flatMap((route) => {
          const metadata = route.options.staticData;

          if (!metadata || !('tab' in metadata) || !metadata.tab) {
            return [];
          }

          const { tab } = metadata as PortalTabbedNavigationTab<TabbedNavigationGroup, TabbedNavigationSubGroup>;

          return [
            {
              routeId: route.id,
              to: route.fullPath,
              tab,
            },
          ];
        }),
      ),
    }),
    [apiContext, isAuthenticated, isAuthenticating],
  );

  useEventPoller();

  return <RouterProvider context={values} router={router} />;
}

function onMfaRequired() {
  if (window.location.pathname !== MFA_SETUP_ROUTE) {
    window.location.href = MFA_SETUP_ROUTE;
  }
}

function onUnauthenticated() {
  const redirectTo = getRedirectParameter(decodeURIComponent(window.location.pathname));
  const redirectParams = redirectTo ? `?redirectTo=${redirectTo}` : '';

  window.location.href = `${DEFAULT_UNAUTHENTICATED_ROUTE}${redirectParams}`;
}

(() => {
  createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
      <WrappedThemeProvider>
        <WrappedTimeZoneProvider>
          <div className={styles.main}>
            <QueryClientProvider client={getQueryClient()}>
              <AuthorizationProvider onMfaRequired={onMfaRequired} onUnauthenticated={onUnauthenticated}>
                <WrappedAccessProvider>
                  <WrappedRouter />
                </WrappedAccessProvider>
              </AuthorizationProvider>
            </QueryClientProvider>
            <ToastRail />
            <RecommendationNagRail />
          </div>
        </WrappedTimeZoneProvider>
      </WrappedThemeProvider>
    </StrictMode>,
  );
})();
